import { z } from "zod";
import { workflowSchema } from "./common";

const optionSchema = z.object({
  id: z.union([z.string(), z.number()]),
  label: z.string(),
  depth: z.number().optional(),
});
const itemSchema = z.object({
  id: z.number().optional(),
  kind: z.string(),
  title: z.string(),
  description: z.string(),
  icon: z.string(),
  order: z.number(),
  active: z.boolean().default(true),
});
const videoItemSchema = z.object({ id:z.number(), key:z.string(), order:z.number(), is_active:z.boolean(), source:z.string(), title:z.string(), description:z.string(), cover_asset_id:z.string().nullable(), cover_url:z.string(), cover_alt:z.string() });
export const videoBlockSchema = z.object({ id:z.number(), mode:z.enum(["single","gallery","playlist"]), single_source:z.string(), playlist_source:z.string(), playlist_cover_source:z.string(), single_cover_asset_id:z.string().nullable(), single_cover_url:z.string(), single_cover_alt:z.string(), playlist_cover_asset_id:z.string().nullable(), playlist_cover_url:z.string(), playlist_cover_alt:z.string(), items:z.array(videoItemSchema) });
export const detailSectionSchema = z.object({ id:z.number(), key:z.string(), order:z.number(), is_active:z.boolean(), text_alignment:z.enum(["left","center","right"]).default("left"), role:z.enum(["discovery","explanation","action","reading"]).default("explanation"), eyebrow:z.string(), title:z.string(), description:z.string(), video:videoBlockSchema.nullable() });
export type DetailSectionDto = z.infer<typeof detailSectionSchema>;
const serviceSectionSchema = z.object({ id:z.number(), key:z.enum(["content","benefits","process","video","finance_models","related","faq"]), order:z.number(), is_active:z.boolean(), text_alignment:z.enum(["left","center","right"]).default("left"), role:z.enum(["discovery","explanation","action","reading"]).default("explanation"), eyebrow:z.string(), title:z.string(), description:z.string(), video:videoBlockSchema.nullable() });
const financeSectionSchema = z.object({ id:z.number(), key:z.enum(["content","criteria","process","video","faq"]), order:z.number(), is_active:z.boolean(), text_alignment:z.enum(["left","center","right"]).default("left"), role:z.enum(["discovery","explanation","action","reading"]).default("explanation"), eyebrow:z.string(), title:z.string(), description:z.string(), video:videoBlockSchema.nullable() });
const translationSchema = z.object({
  title: z.string(),
  slug: z.string(),
  summary: z.string(),
  image_alt: z.string(),
  seo_title: z.string(),
  seo_description: z.string(),
});

const baseSchema = z.object({
  id: z.string(),
  locale: z.string(),
  image: z.string(),
  version: z.number(),
  workflow: workflowSchema,
  allowed_actions: z.array(z.string()),
  locale_states: z.array(
    z.object({
      code: z.string(),
      label: z.string(),
      has_translation: z.boolean(),
      published: z.boolean(),
      workflow: workflowSchema,
    }),
  ),
  revisions: z.array(
    z.object({
      version: z.number(),
      schema_version: z.number(),
      created_at: z.string(),
      created_by: z.string(),
      changed_fields: z.array(z.string()),
      is_draft: z.boolean(),
      is_live: z.boolean(),
    }),
  ),
  options: z.object({
    services: z.array(optionSchema),
    finance_solutions: z.array(optionSchema),
    sectors: z.array(optionSchema),
    classifications: z.array(optionSchema),
    references: z.array(optionSchema),
    companies: z.array(optionSchema).default([]),
    authors: z.array(optionSchema),
    editorial_collections: z.array(optionSchema),
    editorial_categories: z.array(optionSchema),
    editorial_tags: z.array(optionSchema),
    finance_categories: z.array(optionSchema).default([]),
    service_categories: z.array(optionSchema).default([]),
  }),
});

export const serviceEditorSchema = baseSchema.extend({
  record: z.object({
    code: z.string(),
    category_id: z.string(),
    parent_id: z.string().nullable(),
    depth: z.number(),
    icon: z.string(),
    order: z.number(),
    is_archived: z.boolean(),
    media_asset_id: z.string().nullable(),
  }),
  translation: translationSchema,
  content: z.object({
    body: z.string(),
    items: z.array(itemSchema),
    sections: z.array(serviceSectionSchema).optional().default([]),
  }),
  relations: z.object({
    related_service_ids: z.array(z.string()),
    finance_solution_ids: z.array(z.string()).optional().default([]),
  }),
});

export const financeEditorSchema = baseSchema.extend({
  record: z.object({
    code: z.string(),
    category_id: z.string(),
    order: z.number(),
    is_archived: z.boolean(),
    media_asset_id: z.string().nullable(),
  }),
  translation: translationSchema.extend({
    body: z.string(),
  }),
  content: z.object({ items: z.array(itemSchema), sections: z.array(financeSectionSchema).optional().default([]) }),
  relations: z.object({}),
  comparison: z.object({
    category_code: z.string(),
    profile_enabled: z.boolean(),
    published: z.boolean(),
    published_as_of: z.string().nullable(),
    versions: z.array(z.object({
      edition_id: z.string(),
      code: z.string(),
      status: z.string(),
      status_label: z.string(),
      as_of_date: z.string(),
      is_current: z.boolean(),
      rows: z.array(z.object({
        criterion_key: z.string(),
        group: z.string(),
        label: z.string(),
        value: z.string(),
        note: z.string(),
        missing: z.boolean(),
      })),
    })),
  }),
});

export const editorialEditorSchema = baseSchema.extend({
  record: z.object({
    format: z.string(),
    author_id: z.string().nullable(),
    category_id: z.string().nullable(),
    collection_ids: z.array(z.string()),
    tag_ids: z.array(z.string()),
    sector_ids: z.array(z.string()),
    order: z.number(),
    is_featured: z.boolean(),
    is_archived: z.boolean(),
    media_asset_id: z.string().nullable(),
  }),
  translation: translationSchema,
  content: z.object({
    body: z.string(),
    transcript: z.string(),
    outcomes: z.string(),
    video_url: z.string(),
    duration_seconds: z.number().nullable(),
    resource_url: z.string(),
    resource_version: z.string(),
    event_date: z.string().nullable(),
    duration_minutes: z.number().nullable(),
    source_url: z.string(),
    publication_name: z.string(),
    resource_media_asset_id: z.string().nullable().optional(),
    sections: z.array(detailSectionSchema).optional().default([]),
  }),
  relations: z.object({}).passthrough(),
});

export type ServiceEditorDto = z.infer<typeof serviceEditorSchema>;
export type FinanceEditorDto = z.infer<typeof financeEditorSchema>;
export type EditorialEditorDto = z.infer<typeof editorialEditorSchema>;
